import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { IBrand as IFullBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import { IBrandPromoTile } from "@faire/web-api--source/indigofair/data/IBrandPromoTile";
import { IBrandTile } from "@faire/web-api--source/indigofair/data/IBrandTile";
import { ILargeBrandTile } from "@faire/web-api--source/indigofair/data/ILargeBrandTile";
import { ILeanBrandTile as IRealLeanBrandTile } from "@faire/web-api--source/indigofair/data/ILeanBrandTile";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { ISearchBrandsV2Response } from "@faire/web-api--source/indigofair/data/ISearchBrandsV2Response";

import { getLeadTimeText } from "@faire/retailer/lib/getLeadTimeText";

export type ILeanBrandTile = IRealLeanBrandTile;

export namespace ILeanBrandTile {
  export type IBrand = IRealLeanBrandTile.IBrand;

  export const fromLeanProductTileBrand = (
    brand?: ILeanProductTile.IBrand
  ): ILeanBrandTile | undefined => {
    if (!brand) {
      return undefined;
    }

    return {
      brand: IRealLeanBrandTile.IBrand.build({
        name: brand.name,
        token: brand.token,
        squared_image: brand.squared_image,
        free_shipping_reason: brand.free_shipping_reason,
        minimum_order_amount: brand.minimum_order_amount,
        is_top_shop: brand.is_top_shop,
        is_highlighted_brand_for_promotional_event:
          brand.is_highlighted_brand_for_promotional_event,
        based_in: brand.based_in,
        ships_from_country: brand.ships_from_country,
        based_in_country: brand.based_in,
        profile_image: brand.profile_image,
        insider_benefits: brand.insider_benefits,
        insider_label: brand.insider_label,
        active_products_count: brand.active_products_count,
        based_in_country_message: brand.based_in_country_message,
        region_flag_image: brand.region_flag_image,
        avg_brand_review_rating: brand.avg_brand_review_rating,
      }),
      promo_text: undefined,
      images: [],
      brand_promo_label: undefined,
      featured_brand_promo_label: undefined,
      lead_time_text: undefined,
    };
  };

  export const fromIBrandTile = (
    brand?: IBrandTile
  ): ILeanBrandTile | undefined => {
    if (!brand) {
      return undefined;
    }

    return {
      brand: IRealLeanBrandTile.IBrand.build({
        name: brand.name,
        token: brand.token,
        squared_image: brand.image,
        is_following: brand.is_following,
        free_shipping_reason: brand.free_shipping_reason,
        minimum_order_amount: brand.minimum_order_amount,
        has_new_products: brand.has_new_products,
        is_top_shop: brand.is_top_shop,
        is_highlighted_brand_for_promotional_event:
          brand.is_highlighted_brand_for_promotional_event,
        based_in: brand.based_in_country,
        ships_from_country: brand.ships_from_country,
        is_showroom: brand.is_showroom,
        based_in_country: brand.based_in_country,
        insider_benefits: brand.insider_benefits,
        insider_label: brand.insider_label,
        region_flag_image: brand.region_flag_image,
        avg_brand_review_rating: brand.avg_brand_review_rating,
        product_images: brand.product_images,
      }),
      promo_text: brand.brand_promo_string,
      images: [brand.image].filter(isNotUndefined),
      brand_promo_label: brand.brand_promo_label,
      featured_brand_promo_label: brand.featured_brand_promo_label,
      lead_time_text: undefined,
    };
  };

  export const fromIBrand = (
    brand?: IFullBrand
  ): ILeanBrandTile | undefined => {
    if (!brand) {
      return undefined;
    }

    return {
      brand: IRealLeanBrandTile.IBrand.build({
        name: brand.name,
        is_following: brand.is_following,
        token: brand.token,
        ships_from_country: brand.ships_from_country,
        description: brand.description,
        squared_image: brand.squared_image,
        free_shipping_reason: brand.free_shipping_reason,
        has_new_products: brand.has_new_products,
        is_first_order: brand.is_first_order,
        minimum_order_amount: brand.minimum_order_amount,
        is_highlighted_brand_for_promotional_event:
          brand.is_highlighted_brand_for_promotional_event,
        is_top_shop: brand.is_top_shop,
        // TODO(danyao.wang): remove this field once BrandTileForCarousel is updated to
        // take an ILeanBrandTile instead of ILeanBrandTile.IBrand.
        based_in: brand.based_in,
        insider_benefits: brand.insider_benefits,
        insider_label: brand.insider_label,
        is_showroom: brand.is_showroom,
        profile_image: brand.profile_image,
        hide_chat: brand.hide_chat,
        brand_reviews_summary: brand.brand_reviews_summary,
        based_in_country: brand.based_in,
        zip_code_protection_enabled: brand.zip_code_protection_enabled,
      }),
      promo_text: brand.brand_promo_string,
      images: brand.images ?? [],
      brand_promo_label: brand.brand_promo_label,
      featured_brand_promo_label: brand.featured_brand_promo_label,
      lead_time_text: undefined,
    };
  };

  export const fromSearchV2IBrand =
    (strictLocalize: StrictLocalizeFunction) =>
    (
      brand: ISearchBrandsV2Response.IBrand | undefined
    ): ILeanBrandTile | undefined => {
      if (!brand) {
        return undefined;
      }

      return {
        brand: IRealLeanBrandTile.IBrand.build({
          name: brand.name,
          token: brand.token,
          squared_image: brand.image,
          description: brand.description,
          is_following: brand.is_following,
          free_shipping_reason: brand.free_shipping_reason,
          minimum_order_amount: brand.minimum_order_amount,
          has_new_products: brand.has_new_products,
          is_top_shop: brand.is_top_shop,
          is_first_order: brand.is_first_order,
          is_highlighted_brand_for_promotional_event:
            brand.is_highlighted_brand_for_promotional_event,
          based_in: brand.based_in_country,
          ships_from_country: brand.ships_from_country,
          is_showroom: brand.is_showroom,
          zip_code_protection_enabled: brand.is_zip_code_protection_enabled,
          based_in_country: brand.based_in_country,
          insider_benefits: brand.insider_benefits,
          insider_label: brand.insider_label,
          show_location_badging: brand.show_location_badging,
          region_flag_image: brand.region_flag_image,
          avg_brand_review_rating: brand.avg_brand_review_rating,
          product_images: brand.product_images,
        }),
        promo_text: brand.highest_tier_promo_description,
        images: [brand.image, ...brand.product_images].filter(isNotUndefined),
        brand_promo_label: brand.brand_promo_label,
        featured_brand_promo_label: brand.featured_promo_label,
        lead_time_text: getLeadTimeText(
          brand.lower_bound_lead_time_days,
          brand.upper_bound_lead_time_days,
          strictLocalize
        ),
      };
    };

  export const fromLargeBrandTile = (
    brand?: ILargeBrandTile
  ): ILeanBrandTile | undefined => {
    if (!brand) {
      return undefined;
    }
    return {
      brand: IRealLeanBrandTile.IBrand.build({
        name: brand.name,
        token: brand.token,
        squared_image: brand.image,
        description: brand.description,
        is_following: brand.is_following,
        free_shipping_reason: brand.free_shipping_reason,
        minimum_order_amount: brand.minimum_order_amount,
        has_new_products: brand.has_new_products,
        is_top_shop: brand.is_top_shop,
        is_highlighted_brand_for_promotional_event:
          brand.is_highlighted_brand_for_promotional_event,
        based_in: brand.based_in_country,
        ships_from_country: brand.ships_from_country,
        is_showroom: brand.is_showroom,
        highlighted_apparel_seasons_for_promotional_event:
          brand.highlighted_apparel_seasons_for_promotional_event,
        based_in_country: brand.based_in_country,
        insider_benefits: brand.insider_benefits,
        insider_label: brand.insider_label,
        profile_image: brand.profile_image,
        show_location_badging: brand.show_location_badging,
        region_flag_image: brand.region_flag_image,
        product_images: brand.product_images,
      }),
      promo_text: brand.brand_promo_string,
      images: [brand.image, ...brand.product_images].filter(isNotUndefined),
      brand_promo_label: brand.brand_promo_label,
      featured_brand_promo_label: brand.featured_brand_promo_label,
      lead_time_text: undefined,
    };
  };

  export const fromBoardLeanBrandTile = (
    brandTile: ILeanBrandTile
  ): ILeanBrandTile => {
    return {
      ...brandTile,
      images:
        brandTile.images.length === 0
          ? [brandTile.brand?.squared_image].filter(isNotUndefined)
          : brandTile.images,
    };
  };

  export const fromBrandPromoTile = (
    brandPromoTile: IBrandPromoTile
  ): ILeanBrandTile =>
    IRealLeanBrandTile.build({
      brand: IRealLeanBrandTile.IBrand.build({
        name: brandPromoTile.name,
        token: brandPromoTile.token,
        squared_image: brandPromoTile.image,
        minimum_order_amount: brandPromoTile.minimum_order_amount,
        is_top_shop: brandPromoTile.is_top_shop,
        is_highlighted_brand_for_promotional_event:
          brandPromoTile.is_highlighted_brand_for_promotional_event,
        ships_from_country: brandPromoTile.ships_from_country,
      }),
      promo_text:
        brandPromoTile.active_brand_promo_collection?.promos[
          (brandPromoTile.active_brand_promo_collection?.promos.length ?? 0) - 1
        ]?.short_description,
      images: brandPromoTile.image ? [brandPromoTile.image] : [],
      brand_promo_label: undefined,
      featured_brand_promo_label: undefined,
      lead_time_text: undefined,
    });
}
